import i18next from "src/i18n/config";

import { Device, DeviceWarning, Group, License, PolicyStatus } from "@dashboard/devices/types";
import { Theme } from "@mui/material";

export type LicenseActivationStatusKey = "Used" | "Unused";

export const getLicenseActivationStatusKey = (license: License): LicenseActivationStatusKey => {
    return license?.device ? "Used" : "Unused";
};

export const getLicenseActivationStatusLabel = (status: LicenseActivationStatusKey) => {
    switch (status) {
        case "Used":
            return i18next.t("common:status.used");
        case "Unused":
        default:
            return i18next.t("common:status.unused");
    }
};

const emptyArrayHash = "d41d8cd98f00b204e9800998ecf8427e";

/**
 * A group has an effective policy only when one is assigned and it contains at least one rule.
 */
export const hasEffectivePolicy = (group: Pick<Group, "policyHash"> | undefined) =>
    !!group?.policyHash && group.policyHash !== emptyArrayHash;

export function getDevicePolicyStatus(
    device: API.Device | Device | null,
    groupOrGroups: Group | Group[] | undefined,
): PolicyStatus | null {
    if (!device) {
        return null;
    }
    const group = Array.isArray(groupOrGroups)
        ? groupOrGroups.find((g) => g.licenseIds.includes(device.licenseId))
        : groupOrGroups;

    if (!hasEffectivePolicy(group)) {
        return PolicyStatus.NO_GROUP_POLICY;
    }

    if (device.lastState?.policy?.hash === group?.policyHash) {
        return device.lastState?.policy?.successful ? PolicyStatus.COMPLIANT : PolicyStatus.NON_COMPLIANT;
    }

    return PolicyStatus.NON_COMPLIANT;
}

type PolicyStatusKey = "Compliant" | "Non-compliant" | "No policy";

export const policyStatusKey = (status: PolicyStatus | null): PolicyStatusKey | null => {
    switch (status) {
        case PolicyStatus.COMPLIANT:
            return "Compliant";
        case PolicyStatus.NON_COMPLIANT:
            return "Non-compliant";
        case PolicyStatus.NO_GROUP_POLICY:
            return "No policy";
        default:
            return null;
    }
};

const getNonComplianceReason = (device: API.Device | Device, group: Group | undefined): string | null => {
    if (!device.lastState) {
        return i18next.t("sidepanels:devicePanel.policyReason.noDeviceData");
    }
    const policy = device.lastState.policy;
    if (!policy) {
        return i18next.t("sidepanels:devicePanel.policyReason.notReported");
    }
    if (policy.hash && group?.policyHash && policy.hash !== group.policyHash) {
        return i18next.t("sidepanels:devicePanel.policyReason.updatePending");
    }
    const hasErrors = policy.errors ? Object.keys(policy.errors).length > 0 : false;
    if (hasErrors || policy.successful === false) {
        return i18next.t("sidepanels:devicePanel.policyReason.runtimeProblem");
    }
    return null;
};

/**
 * The label for a device's policy status, status first and the reason in parentheses, e.g. "Non-compliant (Update
 * pending)" or "Not available (No policy)". Use it wherever a single device's policy status is shown.
 */
export const getPolicyStatusLabel = (device: API.Device | Device | null, group: Group | undefined): string | null => {
    const status = getDevicePolicyStatus(device, group);
    if (!device || !status) {
        return null;
    }
    switch (status) {
        case PolicyStatus.NO_GROUP_POLICY:
            return group?.policyId
                ? i18next.t("pages:devicePage.options.notAvailableEmptyPolicy")
                : i18next.t("pages:devicePage.options.notAvailableNoPolicy");
        case PolicyStatus.COMPLIANT:
            return i18next.t("pages:devicePage.options.compliant");
        case PolicyStatus.NON_COMPLIANT: {
            const reason = getNonComplianceReason(device, group);
            return reason
                ? i18next.t("pages:devicePage.options.nonCompliantWithReason", { reason })
                : i18next.t("pages:devicePage.options.nonCompliant");
        }
        default:
            return null;
    }
};

export const getPolicyIconColor = (status: PolicyStatus | null, theme: Theme): string => {
    if (status === PolicyStatus.COMPLIANT) {
        return theme.palette.emteria.success;
    }
    if (status === PolicyStatus.NON_COMPLIANT) {
        return theme.palette.emteria.warning;
    }
    return theme.palette.emteria.text.secondary;
};

export const getSummaryIndicatorData = (
    license: License | null,
    deviceWarnings: DeviceWarning[],
    isExpired: boolean,
    theme: Theme,
    isValid?: boolean,
): {
    tooltip: string;
    color: string;
} => {
    const device = license?.device;

    if (isExpired) {
        return {
            tooltip: i18next.t("pages:devicePage.status.subscriptionExpired"),
            color: theme.palette.emteria.background.paperDeep,
        };
    } else if (!isValid) {
        return {
            tooltip: i18next.t("pages:devicePage.status.invalidSubscription"),
            color: theme.palette.emteria.warning,
        };
    } else if (!device) {
        return {
            tooltip: i18next.t("pages:devicePage.status.unusedLicense"),
            color: theme.palette.emteria.background.paperDeep,
        };
    } else if (!device?.isOnline) {
        return {
            tooltip: i18next.t("pages:devicePage.status.deviceOffline"),
            color: theme.palette.emteria.error,
        };
    } else if (deviceWarnings.includes("lowMemory")) {
        return {
            tooltip: i18next.t("pages:devicePage.status.lowMemory"),
            color: theme.palette.emteria.warning,
        };
    } else if (deviceWarnings.includes("lowBattery")) {
        return {
            tooltip: i18next.t("pages:devicePage.status.lowBattery"),
            color: theme.palette.emteria.warning,
        };
    } else if (deviceWarnings.includes("nonCompliant")) {
        return {
            tooltip: i18next.t("pages:devicePage.status.policyWarning"),
            color: theme.palette.emteria.warning,
        };
    } else {
        return {
            tooltip: i18next.t("pages:devicePage.status.deviceOnline"),
            color: theme.palette.emteria.success,
        };
    }
};
